import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import { useAtiradores, useEnviarConvite, useEnviarConviteWhatsapp } from '../hooks/useAtiradores'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useEtapas } from '../../etapas/hooks/useEtapas'
import { atiradorService, type ResultadoConvite, type EnvioWhatsapp, type EstadoWhatsapp } from '../services/atiradores.service'

const APP_URL = window.location.origin

const MENSAGEM_PADRAO = `Ola {nome},

Voce esta convidado a participar da 3a Etapa - Grande Final do nosso campeonato de tiro, que esta acontecendo de 30 de outubro a 02 de novembro de 2026.

As inscricoes vao ate 02/11. Garanta sua vaga pelo link abaixo:
{link}

Contamos com sua participacao!`

function digitsOnly(v: string): string {
  return v.replace(/\D/g, '')
}

function substituir(texto: string, alvo: string, valor: string): string {
  return texto.split(alvo).join(valor)
}

// mesma regra do backend (shared/whatsapp.ts): 55 + DDD + numero
function telefoneValido(telefone: string | null | undefined): boolean {
  let d = digitsOnly(telefone ?? '').replace(/^0+/, '')
  if (d.length === 10 || d.length === 11) d = '55' + d
  return /^55\d{10,11}$/.test(d)
}

const ROTULO_ESTADO: Record<EstadoWhatsapp, string> = {
  open: 'conectado',
  connecting: 'aguardando leitura do QR code',
  close: 'desconectado',
  inexistente: 'ainda nao configurado',
}

function buildWhatsAppLink(telefone: string, mensagem: string): string {
  let digits = digitsOnly(telefone)
  if (digits.length === 10 || digits.length === 11) digits = '55' + digits
  return `https://wa.me/${digits}?text=${encodeURIComponent(mensagem)}`
}

export function EnviarConvitesPage() {
  const [selCampeonato, setSelCampeonato] = useState<number | ''>('')
  const [selNumeroEtapa, setSelNumeroEtapa] = useState<number | ''>('')
  const [busca, setBusca] = useState('')
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [assunto, setAssunto] = useState('Convite: 3a Etapa (Grande Final) do Campeonato - 30/10 a 02/11/2026')
  const [mensagem, setMensagem] = useState(MENSAGEM_PADRAO)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [resultado, setResultado] = useState<ResultadoConvite | null>(null)
  const [estadoWpp, setEstadoWpp] = useState<EstadoWhatsapp | null>(null)
  const [erroWpp, setErroWpp] = useState<string | null>(null)
  const [qrcode, setQrcode] = useState<string | null>(null)
  const [gerandoQr, setGerandoQr] = useState(false)
  const [envioWpp, setEnvioWpp] = useState<EnvioWhatsapp | null>(null)

  const { data: campeonatosData } = useCampeonatos({ limit: 200 })
  const { data: etapasData } = useEtapas({ idcampeonato: selCampeonato || undefined, limit: 200 })
  const { data: atiradoresData, isLoading } = useAtiradores({ nome: busca || undefined, limit: 500 })
  const enviarConvite = useEnviarConvite()
  const enviarWhatsapp = useEnviarConviteWhatsapp()

  async function atualizarEstadoWpp() {
    try {
      setEstadoWpp(await atiradorService.whatsappEstado())
      setErroWpp(null)
    } catch (err: any) {
      setErroWpp(err.response?.data?.error ?? 'Nao foi possivel consultar o WhatsApp')
    }
  }

  useEffect(() => { atualizarEstadoWpp() }, [])

  // enquanto o QR code esta na tela, confere a cada 3 s se o celular ja conectou
  useEffect(() => {
    if (!qrcode) return
    const t = setInterval(async () => {
      try {
        const estado = await atiradorService.whatsappEstado()
        setEstadoWpp(estado)
        if (estado === 'open') setQrcode(null)
      } catch { /* tenta de novo no proximo ciclo */ }
    }, 3000)
    return () => clearInterval(t)
  }, [qrcode])

  // acompanha o envio em segundo plano
  const envioWppId = envioWpp && !envioWpp.concluido ? envioWpp.id : null
  useEffect(() => {
    if (!envioWppId) return
    const t = setInterval(async () => {
      try {
        const st = await atiradorService.statusConviteWhatsapp(envioWppId)
        setEnvioWpp(st)
        if (st.concluido) {
          setAlert({
            type: st.falhas.length ? 'error' : 'success',
            message: `${st.enviados.length} WhatsApp enviado(s)${st.falhas.length ? `, ${st.falhas.length} falha(s)` : ''}${st.sem_telefone.length ? `, ${st.sem_telefone.length} sem telefone valido` : ''}`,
          })
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          setEnvioWpp(prev => prev && { ...prev, concluido: true })
          setAlert({ type: 'error', message: 'Perdeu o acompanhamento do envio (a API reiniciou?). Confira no WhatsApp quem recebeu.' })
        }
      }
    }, 3000)
    return () => clearInterval(t)
  }, [envioWppId])

  async function handleConectarWpp() {
    setGerandoQr(true)
    try {
      const qr = await atiradorService.whatsappQrcode()
      setQrcode(qr)
      await atualizarEstadoWpp()
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao gerar o QR code' })
    } finally {
      setGerandoQr(false)
    }
  }

  const etapasNumeroUnicos = useMemo(() => {
    const vistos = new Set<number>()
    const lista: number[] = []
    for (const e of etapasData?.data ?? []) {
      if (!vistos.has(e.numero_etapa)) { vistos.add(e.numero_etapa); lista.push(e.numero_etapa) }
    }
    return lista.sort((a, b) => a - b)
  }, [etapasData])

  useEffect(() => {
    if (!selNumeroEtapa && etapasNumeroUnicos.length > 0) {
      setSelNumeroEtapa(etapasNumeroUnicos.includes(3) ? 3 : etapasNumeroUnicos[0])
    }
  }, [etapasNumeroUnicos, selNumeroEtapa])

  useEffect(() => {
    setSelNumeroEtapa('')
  }, [selCampeonato])

  const atiradores = atiradoresData?.data ?? []
  const linkInscricao = selCampeonato && selNumeroEtapa
    ? `${APP_URL}/inscricao-publica?idcampeonato=${selCampeonato}&numero_etapa=${selNumeroEtapa}`
    : ''

  const todosSelecionados = atiradores.length > 0 && atiradores.every(a => selecionados.has(a.idatirador))

  function toggleTodos() {
    if (todosSelecionados) {
      setSelecionados(new Set())
    } else {
      setSelecionados(new Set(atiradores.map(a => a.idatirador)))
    }
  }

  function toggleUm(id: number) {
    setSelecionados(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const selecionadosComEmail = atiradores.filter(a => selecionados.has(a.idatirador) && a.email)
  const selecionadosSemEmail = atiradores.filter(a => selecionados.has(a.idatirador) && !a.email)
  const selecionadosComTelefone = atiradores.filter(a => selecionados.has(a.idatirador) && telefoneValido(a.telefone))
  const enviandoWpp = enviarWhatsapp.isPending || (envioWpp !== null && !envioWpp.concluido)

  async function handleEnviarWhatsapp() {
    if (!selCampeonato || !selNumeroEtapa) {
      setAlert({ type: 'error', message: 'Selecione o campeonato e a etapa' })
      return
    }
    // como no e-mail: vai para todos os selecionados (inclusive os escondidos pela busca); o backend separa quem nao tem telefone
    const n = selecionados.size
    if (n === 0) {
      setAlert({ type: 'error', message: 'Selecione ao menos um atirador' })
      return
    }
    const minutos = Math.max(1, Math.ceil((n * 4.5) / 60))
    if (!window.confirm(`Enviar o convite por WhatsApp para ${n} atirador(es) selecionado(s)?\n\nAs mensagens saem uma a uma com pausa entre elas (leva uns ${minutos} min). Pode continuar usando o sistema, mas deixe esta tela aberta para ver o resultado.`)) return
    try {
      const envio = await enviarWhatsapp.mutateAsync({
        idatiradores: Array.from(selecionados),
        idcampeonato: selCampeonato,
        numero_etapa: selNumeroEtapa,
        mensagem,
      })
      setEnvioWpp(envio)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao enviar pelo WhatsApp' })
      atualizarEstadoWpp()
    }
  }

  async function handleEnviar() {
    if (!selCampeonato || !selNumeroEtapa) {
      setAlert({ type: 'error', message: 'Selecione o campeonato e a etapa' })
      return
    }
    if (selecionados.size === 0) {
      setAlert({ type: 'error', message: 'Selecione ao menos um atirador' })
      return
    }
    try {
      const res = await enviarConvite.mutateAsync({
        idatiradores: Array.from(selecionados),
        idcampeonato: selCampeonato,
        numero_etapa: selNumeroEtapa,
        assunto,
        mensagem,
      })
      setResultado(res)
      setAlert({
        type: res.falhas.length ? 'error' : 'success',
        message: `${res.enviados.length} e-mail(s) enviado(s)${res.falhas.length ? `, ${res.falhas.length} falha(s)` : ''}${res.sem_email.length ? `, ${res.sem_email.length} sem e-mail cadastrado` : ''}`,
      })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao enviar convites' })
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Enviar Convite de Inscrição" description="Convide atiradores cadastrados a se inscreverem online numa etapa" />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Campeonato" required>
            <select className={selectClass} value={selCampeonato}
              onChange={e => setSelCampeonato(e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">Selecione...</option>
              {campeonatosData?.data?.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} ({c.temporada})</option>
              ))}
            </select>
          </FormField>
          <FormField label="Etapa" required>
            <select className={selectClass} value={selNumeroEtapa} disabled={!selCampeonato}
              onChange={e => setSelNumeroEtapa(e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">Selecione...</option>
              {etapasNumeroUnicos.map(n => (
                <option key={n} value={n}>{n}a Etapa</option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="Assunto do e-mail">
          <input className={inputClass} data-sem-maiusculo value={assunto} onChange={e => setAssunto(e.target.value)} />
        </FormField>

        <FormField label="Mensagem (use {nome} e {link} — sao substituidos para cada atirador)">
          <textarea className={inputClass} data-sem-maiusculo rows={7} value={mensagem} onChange={e => setMensagem(e.target.value)} />
        </FormField>

        {linkInscricao && (
          <p className="text-xs text-compumaq-textoSuave">
            Link que sera enviado: <span className="font-mono">{linkInscricao}</span>
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-compumaq-dourado/25 text-sm">
          <span className="text-compumaq-textoSuave">
            WhatsApp do campeonato:{' '}
            {erroWpp ? <span className="text-red-600">{erroWpp}</span>
              : !estadoWpp ? '...'
              : <span className={estadoWpp === 'open' ? 'text-green-600 font-medium' : 'text-amber-600'}>{ROTULO_ESTADO[estadoWpp]}</span>}
          </span>
          {estadoWpp && estadoWpp !== 'open' && (
            <button onClick={handleConectarWpp} disabled={gerandoQr}
              className="px-3 py-1.5 border border-compumaq-dourado/50 text-compumaq-texto text-xs rounded-lg hover:bg-compumaq-preto disabled:opacity-50">
              {gerandoQr ? 'Gerando...' : qrcode ? 'Gerar outro QR code' : 'Conectar WhatsApp'}
            </button>
          )}
          <button onClick={atualizarEstadoWpp} className="text-xs text-compumaq-textoSuave hover:text-compumaq-texto underline">
            atualizar
          </button>
        </div>
        {qrcode && (
          <div className="flex flex-wrap items-center gap-4">
            <img src={qrcode} alt="QR code do WhatsApp" className="w-56 h-56 bg-white p-2 rounded-lg" />
            <p className="text-sm text-compumaq-textoSuave max-w-sm">
              No celular do numero que vai enviar os convites: WhatsApp &gt; Dispositivos conectados &gt; Conectar dispositivo,
              e leia este QR code. Ele vale poucos segundos; se expirar, clique em "Gerar outro QR code".
            </p>
          </div>
        )}
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        <div className="flex flex-wrap gap-3 items-end p-4 border-b border-compumaq-dourado/25">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Buscar por nome</label>
            <input className={inputClass} value={busca} onChange={e => setBusca(e.target.value)} placeholder="Nome do atirador" />
          </div>
          <div className="text-sm text-compumaq-textoSuave">
            {selecionados.size} selecionado(s)
            {selecionadosSemEmail.length > 0 && (
              <span className="text-amber-600"> ({selecionadosSemEmail.length} sem e-mail cadastrado)</span>
            )}
            {selecionados.size > selecionadosComTelefone.length && (
              <span className="text-amber-600"> ({selecionados.size - selecionadosComTelefone.length} sem telefone valido)</span>
            )}
          </div>
          <button onClick={handleEnviarWhatsapp} disabled={enviandoWpp || estadoWpp !== 'open' || selecionadosComTelefone.length === 0}
            title={estadoWpp !== 'open' ? 'Conecte o WhatsApp do campeonato primeiro' : undefined}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">
            {enviandoWpp && envioWpp ? `Enviando WhatsApp ${envioWpp.processados}/${envioWpp.total}...`
              : enviandoWpp ? 'Iniciando...' : `Enviar WhatsApp (${selecionadosComTelefone.length})`}
          </button>
          <button onClick={handleEnviar} disabled={enviarConvite.isPending || selecionadosComEmail.length === 0}
            className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
            {enviarConvite.isPending ? 'Enviando...' : `Enviar E-mail (${selecionadosComEmail.length})`}
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !atiradores.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="font-medium">Nenhum atirador encontrado</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={todosSelecionados} onChange={toggleTodos} className="rounded border-compumaq-dourado/25" />
                </th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Nome</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Telefone</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">E-mail</th>
                <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">WhatsApp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {atiradores.map(a => (
                <tr key={a.idatirador} className="hover:bg-compumaq-preto">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selecionados.has(a.idatirador)} onChange={() => toggleUm(a.idatirador)}
                      className="rounded border-compumaq-dourado/25" />
                  </td>
                  <td className="px-4 py-3 font-medium text-compumaq-texto">{a.nome}</td>
                  <td className="px-4 py-3 text-compumaq-textoSuave">{a.telefone ?? '—'}</td>
                  <td className="px-4 py-3 text-compumaq-textoSuave">
                    {a.email ?? <span className="text-amber-600">sem e-mail</span>}
                  </td>
                  <td className="px-4 py-3">
                    {a.telefone && linkInscricao ? (
                      <a href={buildWhatsAppLink(a.telefone, substituir(substituir(mensagem, '{nome}', a.nome), '{link}', linkInscricao))}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:text-green-800 font-medium">
                        Abrir WhatsApp
                      </a>
                    ) : (
                      <span className="text-xs text-compumaq-textoSuave">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {envioWpp && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-2 text-sm">
          <p className="font-medium text-compumaq-texto">
            WhatsApp: {envioWpp.concluido ? 'envio concluido' : `enviando ${envioWpp.processados} de ${envioWpp.total}...`}
          </p>
          <div className="h-2 bg-compumaq-preto rounded-full overflow-hidden">
            <div className="h-full bg-green-600 transition-all"
              style={{ width: `${envioWpp.total ? Math.round((envioWpp.processados / envioWpp.total) * 100) : 100}%` }} />
          </div>
          {envioWpp.enviados.length > 0 && (
            <p className="text-green-700">Enviados: {envioWpp.enviados.map(e => e.nome).join(', ')}</p>
          )}
          {envioWpp.sem_telefone.length > 0 && (
            <p className="text-amber-600">Sem telefone valido: {envioWpp.sem_telefone.map(e => e.nome).join(', ')}</p>
          )}
          {envioWpp.falhas.length > 0 && (
            <p className="text-red-700">Falhas: {envioWpp.falhas.map(f => `${f.nome} (${f.erro})`).join('; ')}</p>
          )}
        </div>
      )}

      {resultado && (resultado.enviados.length > 0 || resultado.falhas.length > 0) && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-2 text-sm">
          <p className="font-medium text-compumaq-texto">Resultado do envio</p>
          {resultado.enviados.length > 0 && (
            <p className="text-green-700">Enviados: {resultado.enviados.map(e => e.nome).join(', ')}</p>
          )}
          {resultado.sem_email.length > 0 && (
            <p className="text-amber-600">Sem e-mail cadastrado: {resultado.sem_email.map(e => e.nome).join(', ')}</p>
          )}
          {resultado.falhas.length > 0 && (
            <p className="text-red-700">Falhas: {resultado.falhas.map(f => `${f.nome} (${f.erro})`).join('; ')}</p>
          )}
        </div>
      )}
    </div>
  )
}
